import React from "react"
import { UnorderedList, UnorderedListItem, ListItemText } from "@carrotfertility/carotene-core"
import { FormattedMessage, useIntl } from "react-intl"
import moment from "moment/moment"
import { ExpenseState } from "#/utils/ExpenseState"
import { Reimbursement } from "#/types/reimbursement"
import { useGetFundsAdministration } from "#/components/bank-details/hooks/useGetFundsAdministration"
import type { Invoice } from "#/hooks/useInvoices"

const formatShortDate = (intl: ReturnType<typeof useIntl>, datetime: string) =>
  intl.formatDate(moment(datetime).toDate(), {
    year: "numeric",
    month: "short",
    day: "numeric"
  })

const formatUnixDate = (intl: ReturnType<typeof useIntl>, unixTimestamp: number) =>
  intl.formatDate(moment.unix(unixTimestamp).toDate(), {
    year: "numeric",
    month: "short",
    day: "numeric"
  })

type HistoryEntry = { datetime: string | null; node: React.ReactNode }

function sortHistoryEntries(entries: HistoryEntry[]): React.ReactNode[] {
  const withDates = entries.filter((e): e is HistoryEntry & { datetime: string } => e.datetime !== null)
  const withoutDates = entries.filter((e) => e.datetime === null)

  withDates.sort((a, b) => moment(b.datetime).valueOf() - moment(a.datetime).valueOf())

  return [...withoutDates.map((e) => e.node), ...withDates.map((e) => e.node)]
}

function appendInvoiceReconciliationHistoryEntries(
  entries: HistoryEntry[],
  intl: ReturnType<typeof useIntl>,
  invoice: Invoice
) {
  if (invoice.status === "open") {
    entries.push({
      datetime: moment.unix(invoice.dateCreated).toISOString(),
      node: (
        <FormattedMessage
          defaultMessage="Reconciliation requested on {date}"
          values={{ date: formatUnixDate(intl, invoice.dateCreated) }}
        />
      )
    })
    return
  }
  if (invoice.status === "paid") {
    entries.push({
      datetime: moment.unix(invoice.dateCreated).toISOString(),
      node: (
        <FormattedMessage
          defaultMessage="Reconciliation requested on {date}"
          values={{ date: formatUnixDate(intl, invoice.dateCreated) }}
        />
      )
    })
    entries.push({
      datetime: moment.unix(invoice.datePaid).toISOString(),
      node: (
        <FormattedMessage
          defaultMessage="Reconciled on {date}"
          values={{ date: formatUnixDate(intl, invoice.datePaid) }}
        />
      )
    })
  }
}

export function CardHistoryEntries({ transaction, invoices }: { transaction: Reimbursement; invoices: Invoice[] }) {
  const intl = useIntl()
  const entries: HistoryEntry[] = []

  if (transaction.adjustmentDatetime) {
    entries.push({
      datetime: transaction.adjustmentDatetime,
      node: (
        <FormattedMessage
          defaultMessage="Adjusted on {date}"
          values={{ date: formatShortDate(intl, transaction.adjustmentDatetime) }}
        />
      )
    })
  }

  switch (transaction.expenseState) {
    case ExpenseState.PENDING_MEMBER_INFO: {
      const uploadByDate = moment(transaction.submittedDatetime).toDate()
      uploadByDate.setDate(uploadByDate.getDate() + 30)
      const formattedUploadByDate = intl.formatDate(uploadByDate, { month: "short", day: "numeric" })
      entries.push({
        datetime: null,
        node: (
          <FormattedMessage
            defaultMessage="Current status: Please upload an itemized statement by {formattedDate}"
            values={{ formattedDate: formattedUploadByDate }}
          />
        )
      })
      break
    }
    case ExpenseState.PENDING_CARE_NAV:
    case ExpenseState.ON_HOLD:
      entries.push({ datetime: null, node: <FormattedMessage defaultMessage="Current status: In progress" /> })
      break
    case ExpenseState.NEEDS_MEMBER_UPLOADS:
      entries.push({
        datetime: null,
        node: <FormattedMessage defaultMessage="Current status: Documents requested" />
      })
      break
    case ExpenseState.APPROVED: {
      const paidInvoices = invoices.filter((i) => i.status === "paid")
      if (paidInvoices.length > 0) {
        paidInvoices.forEach((invoice) => appendInvoiceReconciliationHistoryEntries(entries, intl, invoice))
      } else if (transaction.approvalDatetime) {
        entries.push({
          datetime: transaction.approvalDatetime,
          node: (
            <FormattedMessage
              defaultMessage="Approved on {date}"
              values={{ date: formatShortDate(intl, transaction.approvalDatetime) }}
            />
          )
        })
      }
      break
    }
    case ExpenseState.DENIED_RECONCILIATION_REQUESTED: {
      const reconciliationInvoices = invoices.filter((i) => i.status === "open" || i.status === "paid")
      if (reconciliationInvoices.length > 0) {
        reconciliationInvoices.forEach((invoice) => {
          appendInvoiceReconciliationHistoryEntries(entries, intl, invoice)
        })
      }

      break
    }
    case ExpenseState.DENIED_RECONCILED: {
      const reconciliationInvoices = invoices.filter((i) => i.status === "open" || i.status === "paid")
      if (reconciliationInvoices.length > 0) {
        reconciliationInvoices.forEach((invoice) => appendInvoiceReconciliationHistoryEntries(entries, intl, invoice))
      } else {
        entries.push({
          datetime: null,
          node: (
            <FormattedMessage
              defaultMessage="Reconciled on {date}"
              values={{ date: formatShortDate(intl, transaction.deniedDate) }}
            />
          )
        })
      }
      break
    }
  }

  if (transaction.submittedDatetime) {
    entries.push({
      datetime: transaction.submittedDatetime,
      node: (
        <FormattedMessage
          defaultMessage="Card used on {date}"
          values={{ date: formatShortDate(intl, transaction.submittedDatetime) }}
        />
      )
    })
  }

  const isCostShare = transaction.preAuthId && transaction.preAuthCreatedDate
  if (isCostShare && transaction.submittedDatetime) {
    entries.push({
      datetime: transaction.preAuthCreatedDate,
      node: (
        <FormattedMessage
          defaultMessage="Authorized on {date}"
          values={{ date: formatShortDate(intl, transaction.preAuthCreatedDate) }}
        />
      )
    })
  }

  const sortedEntries = sortHistoryEntries(entries)

  return (
    <UnorderedList disablePadding>
      {sortedEntries.map((entry, index) => (
        <UnorderedListItem key={index} disablePadding>
          <ListItemText primary={entry} />
        </UnorderedListItem>
      ))}
    </UnorderedList>
  )
}

export function OopHistoryEntries({ transaction, invoices }: { transaction: Reimbursement; invoices: Invoice[] }) {
  const intl = useIntl()
  const fundsAdministration = useGetFundsAdministration()
  const isBankInfoEnabled = fundsAdministration?.data?.isBankInfoRequired

  const entries: HistoryEntry[] = []

  if (transaction.adjustmentDatetime) {
    entries.push({
      datetime: transaction.adjustmentDatetime,
      node: (
        <FormattedMessage
          defaultMessage="Adjusted on {date}"
          values={{ date: formatShortDate(intl, transaction.adjustmentDatetime) }}
        />
      )
    })
  }

  switch (transaction.expenseState) {
    case ExpenseState.RECEIVED:
      entries.push({ datetime: null, node: <FormattedMessage defaultMessage="Current status: In progress" /> })
      break
    case ExpenseState.NEEDS_MEMBER_UPLOADS:
      entries.push({
        datetime: null,
        node: <FormattedMessage defaultMessage="Current status: Documents requested" />
      })
      break
    case ExpenseState.ON_HOLD:
      entries.push({ datetime: null, node: <FormattedMessage defaultMessage="Current status: In progress" /> })
      break
    case ExpenseState.APPROVED:
    case ExpenseState.INVOICING:
    case ExpenseState.INVOICED: {
      const paidInvoices = invoices.filter((i) => i.status === "paid")
      if (paidInvoices.length > 0) {
        paidInvoices.forEach((invoice) => appendInvoiceReconciliationHistoryEntries(entries, intl, invoice))
      } else if (transaction.approvalDatetime) {
        entries.push({
          datetime: transaction.approvalDatetime,
          node: (
            <FormattedMessage
              defaultMessage="Approved on {date}"
              values={{ date: formatShortDate(intl, transaction.approvalDatetime) }}
            />
          )
        })
      }
      break
    }
    case ExpenseState.PAID:
      if (transaction.reconciliationRequestedExpenseId) {
        const reconciliationInvoices = invoices.filter((i) => i.status === "open" || i.status === "paid")
        reconciliationInvoices.forEach((invoice) => appendInvoiceReconciliationHistoryEntries(entries, intl, invoice))
      }
      if (transaction.paidDatetime) {
        if (isBankInfoEnabled) {
          entries.push({
            datetime: transaction.paidDatetime,
            node: (
              <FormattedMessage
                defaultMessage="Reimbursed on {date}"
                values={{ date: formatShortDate(intl, transaction.paidDatetime) }}
              />
            )
          })
        } else {
          entries.push({
            datetime: transaction.paidDatetime,
            node: (
              <FormattedMessage
                defaultMessage="Sent to benefit sponsor for reimbursement on {date}"
                values={{ date: formatShortDate(intl, transaction.paidDatetime) }}
              />
            )
          })
        }
      }
      if (transaction.approvalDatetime) {
        entries.push({
          datetime: transaction.approvalDatetime,
          node: (
            <FormattedMessage
              defaultMessage="Approved on {date}"
              values={{ date: formatShortDate(intl, transaction.approvalDatetime) }}
            />
          )
        })
      }
      break
    case ExpenseState.DENIED: {
      const reconciliationInvoices = invoices.filter((i) => i.status === "open" || i.status === "paid")
      if (reconciliationInvoices.length > 0) {
        reconciliationInvoices.forEach((invoice) => appendInvoiceReconciliationHistoryEntries(entries, intl, invoice))
      } else if (transaction.deniedDate) {
        entries.push({
          datetime: transaction.deniedDate,
          node: (
            <FormattedMessage
              defaultMessage="Denied on {date}"
              values={{ date: formatShortDate(intl, transaction.deniedDate) }}
            />
          )
        })
      }
      break
    }
  }

  if (transaction.submittedDatetime) {
    entries.push({
      datetime: transaction.submittedDatetime,
      node: (
        <FormattedMessage
          defaultMessage="Created on {date}"
          values={{ date: formatShortDate(intl, transaction.submittedDatetime) }}
        />
      )
    })
  }

  const sortedEntries = sortHistoryEntries(entries)

  return (
    <UnorderedList disablePadding>
      {sortedEntries.map((entry, index) => (
        <UnorderedListItem key={index} disablePadding>
          <ListItemText primary={entry} />
        </UnorderedListItem>
      ))}
    </UnorderedList>
  )
}

export function DirectPayHistoryEntries({ transaction }: { transaction: Reimbursement }) {
  const intl = useIntl()

  const entries: HistoryEntry[] = []

  if (transaction.adjustmentDatetime) {
    entries.push({
      datetime: transaction.adjustmentDatetime,
      node: (
        <FormattedMessage
          defaultMessage="Adjusted on {date}"
          values={{ date: formatShortDate(intl, transaction.adjustmentDatetime) }}
        />
      )
    })
  }

  if (transaction.originalPaidDatetime) {
    entries.push({
      datetime: transaction.originalPaidDatetime,
      node: (
        <FormattedMessage
          defaultMessage="Paid on {date}"
          values={{ date: formatShortDate(intl, transaction.originalPaidDatetime) }}
        />
      )
    })
  }

  switch (transaction.expenseState) {
    case ExpenseState.PAID:
      if (transaction.paidDatetime) {
        entries.push({
          datetime: transaction.paidDatetime,
          node: (
            <FormattedMessage
              defaultMessage="Paid on {date}"
              values={{ date: formatShortDate(intl, transaction.paidDatetime) }}
            />
          )
        })
      }
      break
    case ExpenseState.DENIED:
      if (transaction.deniedDate) {
        entries.push({
          datetime: transaction.deniedDate,
          node: (
            <FormattedMessage
              defaultMessage="Denied on {date}"
              values={{ date: formatShortDate(intl, transaction.deniedDate) }}
            />
          )
        })
      }
      break
    case ExpenseState.CANCELLED:
      if (transaction.deniedDate) {
        entries.push({
          datetime: transaction.deniedDate,
          node: (
            <FormattedMessage
              defaultMessage="Canceled on {date}"
              values={{ date: formatShortDate(intl, transaction.deniedDate) }}
            />
          )
        })
      }
      break
    case ExpenseState.SCHEDULED:
      entries.push({ datetime: null, node: <FormattedMessage defaultMessage="Scheduled" /> })
      break
    case ExpenseState.ON_HOLD:
      entries.push({ datetime: null, node: <FormattedMessage defaultMessage="On hold" /> })
      break
    case ExpenseState.RECEIVED:
      entries.push({ datetime: null, node: <FormattedMessage defaultMessage="Received" /> })
      break
    case ExpenseState.NEEDS_PROVIDER_INFO:
      entries.push({ datetime: null, node: <FormattedMessage defaultMessage="Needs provider info" /> })
      break
    case ExpenseState.NEEDS_MEMBER_UPLOADS:
      entries.push({ datetime: null, node: <FormattedMessage defaultMessage="Needs member uploads" /> })
      break
    case ExpenseState.PENDING_NEXT_PLAN_YEAR:
      entries.push({ datetime: null, node: <FormattedMessage defaultMessage="Pending next plan year" /> })
      break
    // BILL_SUBMITTED, APPROVED, INVOICING, INVOICED only show "Created on" date
    case ExpenseState.BILL_SUBMITTED:
    case ExpenseState.APPROVED:
    case ExpenseState.INVOICING:
    case ExpenseState.INVOICED:
      break
  }

  if (transaction.submittedDatetime) {
    entries.push({
      datetime: transaction.submittedDatetime,
      node: (
        <FormattedMessage
          defaultMessage="Created on {date}"
          values={{ date: formatShortDate(intl, transaction.submittedDatetime) }}
        />
      )
    })
  }

  const sortedEntries = sortHistoryEntries(entries)

  return (
    <UnorderedList disablePadding>
      {sortedEntries.map((entry, index) => (
        <UnorderedListItem key={index} disablePadding>
          <ListItemText primary={entry} />
        </UnorderedListItem>
      ))}
    </UnorderedList>
  )
}
